/** Pear Fleet v2 constants — shared by dashboard, login, and BusTracking portal. */

export const FleetProductType = {
  SELF_TRACKING: 'SELF_TRACKING',
  CARGO: 'CARGO',
  FULL_FEATURE: 'FULL_FEATURE',
  LEGACY_BUS_ONLY: 'BUS_ONLY',
  LEGACY_LORRY: 'LORRY',
  /** Trasse Families — one household is the organization. */
  FAMILIES: 'FAMILIES',
} as const;

export type FleetProductTypeValue = (typeof FleetProductType)[keyof typeof FleetProductType];

export const FleetOrgSuffix = {
  SELF_TRACKING: '.fleet',
  CARGO: '.cargo',
  LEGACY_BUS: '.bus',
  LEGACY_LORRY: '.lorry',
  FAMILY: '.family',
} as const;

/** Raw product type uppercased — do NOT collapse BUS_ONLY into SELF_TRACKING. */
export function rawProductType(productType: string | null | undefined): string {
  return (productType ?? '').toUpperCase();
}

/** For collection routing / mobile — legacy aliases map to v2 behaviour. */
export function normalizeProductType(productType: string | undefined): string {
  const pt = rawProductType(productType);
  if (pt === FleetProductType.LEGACY_BUS_ONLY) return FleetProductType.SELF_TRACKING;
  if (pt === FleetProductType.LEGACY_LORRY) return FleetProductType.CARGO;
  return pt || FleetProductType.FULL_FEATURE;
}

export function isFleetProductType(productType: string | undefined): boolean {
  const pt = normalizeProductType(productType);
  return pt === FleetProductType.SELF_TRACKING || pt === FleetProductType.CARGO;
}

/** Trasse Families — a household (.family). The parent owns the org, the family
 *  car is the fleet. Never routed into the school fleet console. */
export function isFamilyOrg(productType: string | null | undefined): boolean {
  return rawProductType(productType) === FleetProductType.FAMILIES;
}

/** School bus tracking — driver + parent only (.bus). */
export function isBusTrackingOrg(productType: string | null | undefined): boolean {
  return rawProductType(productType) === FleetProductType.LEGACY_BUS_ONLY;
}

/** Commercial lorry — owner/driver self-tracking (.fleet). */
export function isLorryPersonalOrg(productType: string | null | undefined): boolean {
  return rawProductType(productType) === FleetProductType.SELF_TRACKING;
}

/** Commercial lorry — cargo + clients (.cargo / legacy .lorry). */
export function isCargoOrg(productType: string | null | undefined): boolean {
  const pt = rawProductType(productType);
  return pt === FleetProductType.CARGO || pt === FleetProductType.LEGACY_LORRY;
}

/** Any org shown under the Lorry admin tab. */
export function isLorryOrg(productType: string | null | undefined): boolean {
  return isLorryPersonalOrg(productType) || isCargoOrg(productType);
}

/** @deprecated use isBusTrackingOrg or isLorryPersonalOrg */
export function isSelfTrackingOrg(productType: string | null | undefined): boolean {
  return isBusTrackingOrg(productType) || isLorryPersonalOrg(productType);
}

export function orgSuffixForProductType(productType: string | null | undefined): string {
  const pt = rawProductType(productType);
  if (pt === FleetProductType.FAMILIES) return FleetOrgSuffix.FAMILY;
  if (pt === FleetProductType.CARGO) return FleetOrgSuffix.CARGO;
  if (pt === FleetProductType.LEGACY_LORRY) return FleetOrgSuffix.LEGACY_LORRY;
  if (pt === FleetProductType.SELF_TRACKING) return FleetOrgSuffix.SELF_TRACKING;
  if (pt === FleetProductType.LEGACY_BUS_ONLY) return FleetOrgSuffix.LEGACY_BUS;
  return '';
}

export function normalizeOrgCodeInput(code: string, productType: string | null | undefined): string {
  const suffix = orgSuffixForProductType(productType);
  if (!suffix) return code.trim();
  const lower = code.trim().toLowerCase();
  if (lower.endsWith(suffix)) return code.trim();
  if (suffix === FleetOrgSuffix.CARGO && lower.endsWith(FleetOrgSuffix.LEGACY_LORRY)) {
    return code.trim().slice(0, -FleetOrgSuffix.LEGACY_LORRY.length) + FleetOrgSuffix.CARGO;
  }
  if (suffix === FleetOrgSuffix.SELF_TRACKING && lower.endsWith(FleetOrgSuffix.LEGACY_BUS)) {
    return code.trim().slice(0, -FleetOrgSuffix.LEGACY_BUS.length) + FleetOrgSuffix.SELF_TRACKING;
  }
  const base = lower.endsWith(suffix) ? code.trim().slice(0, -suffix.length) : code.trim();
  return base + suffix;
}

export const FLEET_ORG_PRODUCT_TYPES = [
  FleetProductType.LEGACY_BUS_ONLY,
  FleetProductType.LEGACY_LORRY,
  FleetProductType.SELF_TRACKING,
  FleetProductType.CARGO,
] as const;

export type LorryMode = typeof FleetProductType.SELF_TRACKING | typeof FleetProductType.CARGO;

const ALL_FLEET_SUFFIXES = [
  FleetOrgSuffix.SELF_TRACKING,
  FleetOrgSuffix.CARGO,
  FleetOrgSuffix.LEGACY_BUS,
  FleetOrgSuffix.LEGACY_LORRY,
  FleetOrgSuffix.FAMILY,
] as const;

function stripFleetSuffix(rawCode: string): { base: string; error?: string } {
  let base = rawCode.trim();
  if (!base) return { base: '', error: 'Organization code is required.' };

  const lower = base.toLowerCase();
  for (const s of ALL_FLEET_SUFFIXES) {
    if (lower.endsWith(s)) {
      base = base.slice(0, -s.length);
      break;
    }
  }

  if (!base.match(/^[a-zA-Z0-9_-]+$/)) {
    return {
      base: '',
      error: 'Codes may only contain letters, numbers, underscores, and hyphens (e.g. HAULAGE1).',
    };
  }
  if (base.length > 32) {
    return { base: '', error: 'Organization code must be 32 characters or less (before suffix).' };
  }
  return { base };
}

/** Build org code for bus tracking only (.bus). */
export function buildBusOrgCode(rawCode: string): { code: string; error?: string } {
  const { base, error } = stripFleetSuffix(rawCode);
  if (error) return { code: '', error };
  return { code: base + FleetOrgSuffix.LEGACY_BUS };
}

/** Build org code for a household (.family), e.g. OKELLO -> OKELLO.family. */
export function buildFamilyOrgCode(rawCode: string): { code: string; error?: string } {
  const { base, error } = stripFleetSuffix(rawCode);
  if (error) return { code: '', error };
  return { code: base + FleetOrgSuffix.FAMILY };
}

/** Build org code for lorry personal (.fleet) or cargo (.cargo). */
export function buildLorryOrgCode(rawCode: string, mode: LorryMode): { code: string; error?: string } {
  const { base, error } = stripFleetSuffix(rawCode);
  if (error) return { code: '', error };
  const suffix = mode === FleetProductType.CARGO ? FleetOrgSuffix.CARGO : FleetOrgSuffix.SELF_TRACKING;
  return { code: base + suffix };
}

/** @deprecated use buildLorryOrgCode */
export function buildFleetOrgCode(rawCode: string, mode: LorryMode): { code: string; error?: string } {
  return buildLorryOrgCode(rawCode, mode);
}

/** Human-readable roles for each org type. */
export function fleetRolesForProductType(productType: string | null | undefined): string {
  if (isFamilyOrg(productType)) {
    return 'Parent (owner) · Co-parent · Private driver';
  }
  if (isCargoOrg(productType)) {
    return 'Fleet admin (web) · Owner · Driver · Client (cargo tracking app)';
  }
  if (isLorryPersonalOrg(productType)) {
    return 'Fleet admin (web) · Owner · Driver (self-tracking app)';
  }
  if (isBusTrackingOrg(productType)) {
    return 'Fleet admin (web) · Bus driver · Parent (bus tracking app)';
  }
  return '';
}

export function orgTypeBadgeLabel(productType: string | null | undefined): string {
  if (isFamilyOrg(productType)) return 'Families';
  if (isBusTrackingOrg(productType)) return 'Bus tracking';
  if (isLorryPersonalOrg(productType)) return 'Lorry · Personal';
  if (isCargoOrg(productType)) return 'Lorry · Cargo';
  return '';
}
